import React, { useState, useMemo, useCallback, useEffect } from 'react';
import DatePicker from '../../components/DatePicker/DatePicker';
import AppointmentControls from './AppointmentControls/AppointmentControls';
import SearchBar from '../../components/SearchBar/SearchBar';
import Button from '../../components/Button/Button';
import { DndContext } from '@dnd-kit/core';
import styles from './Appointments.module.css';
import ScheduleList from './Schedul/ScheduleList';
// AB#2052 — wall-clock (UTC) appointment datetime formatting.
import {
    formatAppointmentTime as formatAppointmentTimeUtc,
    formatAppointmentDate as formatAppointmentDateUtc,
} from '../../lib/appointmentTime';
import AppointmentDetailsModal from './AppointmentDetailsModal';
import RescheduleAppointmentModal from './RescheduleModal/RescheduleAppointmentModal';
import AddEditAppointment from '../Owner/Appointments/AddEditAppointment/AddEditAppointment';
import { useToast } from '../../contexts/ToastContext';
import { useAppointments } from '../../hooks/useAppointments';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { StaffAppointmentDetailDto } from '../../types/api-responses';
import { AppointmentStatus } from '../../types/enums';
// AB#1901 / AB#60 — Boundary-parse the BE-emitted paymentStatus string so the
// client-side filter compares like-with-like. Without this, the BE returns
// `"Unpaid"` while the filter compared against `2` (PaymentStatus.Unpaid int)
// → the filter silently dropped every row → the empty branch surfaced
// "Could not load appointments." See `docs/features/AB1894/arch-brief.md` §5.
import { parsePaymentStatus } from '../../lib/enumParsers';

export interface ScheduleAppointment {
    id: string;
    service: string;
    customer: string;
    contact: string;
    duration: string;
    price: string;
    status: string;
    image: string;
    time: string;
    date: string;
}

const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const getCurrentMonthYear = () => {
    const date = new Date();
    return `${monthNames[date.getMonth()]}, ${date.getFullYear()}`;
};

const Appointments: React.FC = () => {
    const [scheduleData, setScheduleData] = useState<ScheduleAppointment[]>([]);
    const [selectedDate, setSelectedDate] = useState<string>(() => {
        const today = new Date();
        return today.toLocaleDateString('en-GB');
    });
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    // Add appointment modal state (#1229)
    const [isAddAppointmentOpen, setIsAddAppointmentOpen] = useState(false);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState<StaffAppointmentDetailDto | null>(null);
    const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
    const [rescheduleTarget, setRescheduleTarget] = useState<{ id: number; customerName?: string } | null>(null);

    // Filter states — AB#1901 / AB#60: widened to string to match BE wire shape.
    // `AppointmentControls` now emits the BE enum names (e.g. "Unpaid", "Approved").
    const [paymentStatusFilter, setPaymentStatusFilter] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string | null>(null);

    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

    const {
        getAppointments,
        getAppointmentById,
        updateStatus,
        confirmAppointment,
        cancelAppointment,
        rescheduleAppointment
    } = useAppointments();
    const { showToast } = useToast();

    // AB#2052 — wall-clock (UTC) formatters; the datetime is stamped `…Z` but
    // represents the salon's wall-clock time, so extract it in UTC rather than
    // converting to the viewer's timezone. Shared impl: src/lib/appointmentTime.ts.
    const formatAppointmentDate = useCallback(
        (dateTimeString: string) => formatAppointmentDateUtc(dateTimeString),
        [],
    );

    const formatAppointmentTime = useCallback(
        (dateTimeString: string) => formatAppointmentTimeUtc(dateTimeString, { round15: true }),
        [],
    );

    const handleDateChange = useCallback((date: string) => {
        setSelectedDate(date);
    }, []);

    const refreshAppointments = useCallback(async () => {
        setIsLoading(true);
        setHasError(false);
        try {
            const [dd, mm, yyyy] = selectedDate.split('/').map(Number);
            const d = new Date(yyyy, mm - 1, dd);
            d.setDate(d.getDate() - d.getDay());
            const weekStartStr = `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
            const fromDateParts = weekStartStr.split('/');
            const startDate = `${fromDateParts[2]}-${fromDateParts[1]}-${fromDateParts[0]}`;

            const toDateObj = new Date(parseInt(fromDateParts[2]), parseInt(fromDateParts[1]) - 1, parseInt(fromDateParts[0]));
            toDateObj.setDate(toDateObj.getDate() + 6);
            const endDate = toDateObj.toISOString().split('T')[0];

            const response = await getAppointments({
                startDate,
                endDate,
                status: statusFilter ?? undefined,
                searchTerm: debouncedSearchTerm || undefined,
                page: 1,
                pageSize: 50
            });

            const transformedData: ScheduleAppointment[] = response.appointments.map(appointment => {
                const durationParts = appointment.duration?.split(':').map(Number) || [0, 0];
                const [durationHours, durationMinutes] = durationParts;
                const formattedDuration = `${durationHours}h ${durationMinutes}min`;

                return {
                    id: appointment.id.toString(),
                    service: appointment.serviceName,
                    customer: appointment.customerName,
                    contact: appointment.phoneNumber,
                    duration: formattedDuration,
                    price: `${appointment.amount} AED`,
                    status: appointment.paymentStatusString,
                    image: './assets/icons/avatar.png',
                    time: formatAppointmentTime(appointment.appointmentDate),
                    date: formatAppointmentDate(appointment.appointmentDate)
                };
            });

            // AB#1901 / AB#60 — Apply client-side payment status filter.
            // Both sides are normalized via `parsePaymentStatus` so that whether
            // the row carries the BE string ("Unpaid") or a legacy int (2), the
            // compare succeeds. paymentStatusFilter itself is the BE wire string
            // (from `AppointmentControls`), so `parsePaymentStatus(filter)` is
            // always the matching int — but doing the normalize on BOTH sides
            // makes the invariant explicit and survives future filter sources.
            const filteredFilterInt = paymentStatusFilter
                ? parsePaymentStatus(paymentStatusFilter)
                : null;
            const filteredData = filteredFilterInt != null
                ? transformedData.filter(apt => {
                    const row = response.appointments.find(a => a.id.toString() === apt.id);
                    const rowStatusInt = parsePaymentStatus(row?.paymentStatus);
                    return rowStatusInt === filteredFilterInt;
                })
                : transformedData;

            setScheduleData(filteredData);
        } catch (error) {
            setHasError(true);
            showToast('Unable to process your search request. Please refresh or try again later.', 'error');
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, [selectedDate, statusFilter, paymentStatusFilter, debouncedSearchTerm, getAppointments, showToast, formatAppointmentTime, formatAppointmentDate]);

    const handleDragEnd = useCallback(async (data: ScheduleAppointment[], movedAppointmentData?: { appointmentId: string, newTime: string, newDate: string }) => {
        if (movedAppointmentData) {
            try {
                const [day, month, year] = movedAppointmentData.newDate.split('/').map(Number);
                const [time, period] = movedAppointmentData.newTime.split(' ');
                const [hours, minutes] = time.split(':').map(Number);

                let finalHours = hours;
                if (period === 'PM' && hours !== 12) {
                    finalHours += 12;
                } else if (period === 'AM' && hours === 12) {
                    finalHours = 0;
                }

                const newDateTime = new Date(year, month - 1, day, finalHours, minutes);
                const offsetMin = -newDateTime.getTimezoneOffset();
                const offsetSign = offsetMin >= 0 ? '+' : '-';
                const offsetHours = Math.floor(Math.abs(offsetMin) / 60).toString().padStart(2, '0');
                const offsetMins = (Math.abs(offsetMin) % 60).toString().padStart(2, '0');
                const localWithOffset = `${newDateTime.getFullYear()}-${(newDateTime.getMonth() + 1).toString().padStart(2, '0')}-${newDateTime.getDate().toString().padStart(2, '0')}T${newDateTime.getHours().toString().padStart(2, '0')}:${newDateTime.getMinutes().toString().padStart(2, '0')}:00${offsetSign}${offsetHours}:${offsetMins}`;

                await rescheduleAppointment(parseInt(movedAppointmentData.appointmentId), localWithOffset);
                showToast('Appointment rescheduled successfully.', 'success');
                setScheduleData(data);
            } catch (error) {
                showToast('Unable to reschedule appointment. Please try again.', 'error');
                console.error('Reschedule error:', error);
                refreshAppointments();
            }
        } else {
            setScheduleData(data);
        }
    }, [rescheduleAppointment, showToast, refreshAppointments]);

    const handleDropToPast = useCallback(() => {
        showToast('Cannot reschedule to a past time', 'error');
    }, [showToast]);

    const getDateRange = useCallback((startDate: string) => {
        const [dd, mm, yyyy] = startDate.split('/').map(Number);
        const start = new Date(yyyy, mm - 1, dd);
        return Array.from({ length: 7 }, (_, i) => {
            const currentDate = new Date(start);
            currentDate.setDate(start.getDate() + i);
            return currentDate.toLocaleDateString('en-GB');
        });
    }, []);

    const weekStart = useMemo(() => {
        const [dd, mm, yyyy] = selectedDate.split('/').map(Number);
        const d = new Date(yyyy, mm - 1, dd);
        d.setDate(d.getDate() - d.getDay());
        return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
    }, [selectedDate]);

    const dateRange = useMemo(() => getDateRange(weekStart), [weekStart, getDateRange]);

    const filteredScheduleData = useMemo(() =>
        scheduleData.filter(item => dateRange.includes(item.date)),
        [scheduleData, dateRange]);

    const currentMonthYear = useMemo(() => getCurrentMonthYear(), []);

    useEffect(() => {
        refreshAppointments();
    }, [refreshAppointments]);

    const handleSearchChange = useCallback((query: string) => {
        setSearchTerm(query);
    }, []);

    const handleViewDetails = useCallback(async (appointmentId: string) => {
        try {
            const details = await getAppointmentById(parseInt(appointmentId));
            setSelectedAppointment(details);
            setIsModalOpen(true);
        } catch (error) {
            showToast('Failed to load appointment details', 'error');
        }
    }, [getAppointmentById, showToast]);

    const handleRescheduleFromCard = useCallback(async (appointmentId: string) => {
        try {
            const details = await getAppointmentById(parseInt(appointmentId));
            setRescheduleTarget({ id: details.id, customerName: details.customerName });
            setIsRescheduleOpen(true);
        } catch (error) {
            showToast('Failed to load appointment details', 'error');
        }
    }, [getAppointmentById, showToast]);

    const handleStatusUpdate = useCallback(async (id: number, newStatus: AppointmentStatus) => {
        try {
            await updateStatus(id, newStatus);
            showToast('Appointment status updated successfully.', 'success');
            setIsModalOpen(false);
            setSelectedAppointment(null);
            refreshAppointments();
        } catch (error) {
            showToast('Unable to update appointment status. Please try again.', 'error');
        }
    }, [updateStatus, showToast, refreshAppointments]);

    const handleConfirmAppointment = useCallback(async (id: number) => {
        try {
            await confirmAppointment(id);
            showToast('Appointment confirmed successfully.', 'success');
            setIsModalOpen(false);
            setSelectedAppointment(null);
            refreshAppointments();
        } catch (error) {
            showToast('Unable to confirm appointment. Please try again.', 'error');
        }
    }, [confirmAppointment, showToast, refreshAppointments]);

    const handleCancelAppointment = useCallback(async (id: number, reason?: string) => {
        try {
            await cancelAppointment(id, reason);
            showToast('Appointment canceled successfully.', 'success');
            setIsModalOpen(false);
            setSelectedAppointment(null);
            refreshAppointments();
        } catch (error) {
            showToast('Unable to cancel appointment. Please try again.', 'error');
        }
    }, [cancelAppointment, showToast, refreshAppointments]);

    const handleRescheduleAppointment = useCallback(async (id: number, newDateTime: string) => {
        try {
            await rescheduleAppointment(id, newDateTime);
            showToast('Appointment rescheduled successfully.', 'success');
            setIsModalOpen(false);
            setSelectedAppointment(null);
            refreshAppointments();
        } catch (error) {
            showToast('Unable to reschedule appointment. Please try again.', 'error');
            throw error; // Re-throw so modal can show error
        }
    }, [rescheduleAppointment, showToast, refreshAppointments]);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedAppointment(null);
    }, []);

    const handleDateClick = useCallback((date: string) => {
        // Optional: handle date cell click
    }, []);


    // Determine if we should show the "no results" message for search
    const isSearchActive = debouncedSearchTerm.length > 0;
    const noSearchResults = isSearchActive && filteredScheduleData.length === 0 && !isLoading && !hasError;

    return (
        <div className={styles.appointmentsMainContainer}>
            <div className={styles.controlsHeaderContainer}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <h1 className='xH1'>{currentMonthYear}</h1>
                    <Button label="New appointment +" variant="primary" size="medium" onClick={() => setIsAddAppointmentOpen(true)} />
                </div>
                <div className={styles.searchContainer}>
                    <SearchBar
                        placeholder="Search by customer, phone, or service..."
                        onChange={handleSearchChange}
                        value={searchTerm}
                        showClearButton={true}
                    />
                </div>
                <div className={styles.controlsContainer}>
                    <AppointmentControls
                        onPaymentStatusChange={setPaymentStatusFilter}
                        onStatusChange={setStatusFilter}
                    />
                </div>
            </div>
            <DatePicker
                onDateChange={handleDateChange}
                selectedDate={selectedDate}
                onDateClick={handleDateClick}
            />

            {isLoading ? (
                <div className={styles.loadingContainer}>
                    <div className={styles.spinner}>
                        <svg viewBox="0 0 50 50">
                            <circle cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                        </svg>
                        <span>Loading appointments...</span>
                    </div>
                </div>
            ) : hasError ? (
                <div className={styles.emptyState}>
                    <p>Unable to process your search request. Please refresh or try again later.</p>
                    <Button
                        label="Retry"
                        onClick={refreshAppointments}
                        size="small"
                    />
                </div>
            ) : noSearchResults ? (
                <div className={styles.emptyState}>
                    <p>No appointments found matching your criteria. Please refine your search.</p>
                    <Button
                        label="Clear Search"
                        onClick={() => setSearchTerm('')}
                        size="small"
                        noAppearance={true}
                    />
                </div>
            ) : filteredScheduleData.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No appointments scheduled.</p>
                </div>
            ) : (
                <DndContext>
                    <ScheduleList
                        scheduleData={filteredScheduleData}
                        dateRange={dateRange}
                        onDragEnd={handleDragEnd}
                        onViewDetails={handleViewDetails}
                        onRescheduleFromCard={handleRescheduleFromCard}
                        onDropToPast={handleDropToPast}
                    />
                </DndContext>
            )}

            {selectedAppointment && (
                <AppointmentDetailsModal
                    isOpen={isModalOpen}
                    appointment={selectedAppointment}
                    onClose={handleCloseModal}
                    onStatusUpdate={handleStatusUpdate}
                    onConfirmAppointment={handleConfirmAppointment}
                    onCancelAppointment={handleCancelAppointment}
                    onRescheduleAppointment={handleRescheduleAppointment}
                />
            )}

            {isRescheduleOpen && rescheduleTarget && (
                <RescheduleAppointmentModal
                    isOpen={isRescheduleOpen}
                    appointmentId={rescheduleTarget.id}
                    customerName={rescheduleTarget.customerName}
                    onClose={() => { setIsRescheduleOpen(false); setRescheduleTarget(null); }}
                    onConfirmReschedule={async (id, isoDateTime) => {
                        try {
                            await handleRescheduleAppointment(id, isoDateTime);
                            setIsRescheduleOpen(false);
                            setRescheduleTarget(null);
                            refreshAppointments();
                        } catch (error) {
                            // Error already surfaced via toast in handler
                        }
                    }}
                />
            )}

            {isAddAppointmentOpen && (
                <AddEditAppointment
                    isOpen={isAddAppointmentOpen}
                    onClose={() => setIsAddAppointmentOpen(false)}
                    onSuccess={() => { setIsAddAppointmentOpen(false); refreshAppointments(); }}
                    selectedDate={selectedDate}
                    selectedTime=""
                />
            )}
        </div>
    );
};

export default Appointments;
